import {
  Component, h, Prop, State, Event, EventEmitter,
  Fragment,
} from '@stencil/core';
import { pesCatalogPageTypes, HttpStatusCodes, HMCPlatform, PageNames, TProductCtas, buildPageViewPayload, send, HmcSubcategories } from '@pes-ui/utils';
import { RecentlyViewedConfig } from '@pes-ui/components/dist/types/global-types/common';
import {
  TSubcategoryData,
  TSubcategoryListingData,
} from '@pes-ui/components/dist/types/components/pes-app-subcategory/types';
import { IProductsCarouselData } from '@pes-ui/components/dist/types/components/products-carousel/types';
import { PesRoute } from '@pes-ui/components/dist/collection';
import type { THMCBanner } from '@pes-ui/utils';

import { SUBCATEGORY_HMC_KEY } from './consts';
import { expAPIEndpoint, getExpAPIEndpointURL } from '../../../globals/utils/exp-api-urls-builder';
import { SubcategoryContextTemplate } from '../pes-app-spryker-category-container/types';

import {
  MAX_RECENTLY_VIEW_PRODUCTS,
  getData,
  requestRecentlyViewedProducts,
} from '../../../globals/utils';
import { MY_SCHNEIDER_CLICK_EVENT } from '../../../globals/constants';

@Component({
  tag: 'pes-app-spryker-subcategory-container',
  shadow: true,
})
export class PesAppSprykerSubcategoryContainer {
  /**
   * The host of the endpoints.
   * @example https://www.se.com:9090
   */
  @Prop() host!: string;

  /**
   * The code of the country of the app.
   * @example fr
   */
  @Prop() countryCode!: string;

  /**
   * The code of the language of the app.
   * @example it
   */
  @Prop() languageCode!: string;

  /**
   * The ID of the category.
   * @example 4040
   */
  @Prop() subcategoryId!: string;

  /**
   * Defines whether to show the breadcrumbs.
   */
  @Prop() showBreadcrumbs: boolean;

  /**
   * Setup productCtas logic.
   * buttons - predefined CTA buttons.
   * productProp - predefined property of product to find in list.
   */
  @Prop() productCtas: TProductCtas;

  /**
   * Show Recently Viewed section
   */
  @Prop() showRecentlyViewed!: boolean;

  /**
   * Defines whether to show the documents.
   */
  @Prop() showDocuments = true;

  /**
   * Defines whether to show the buy online.
   */
  @Prop() showBuyOnline = true;

  /**
   * Defines whether to show the commerce connector.
   */
  @Prop() enableCommerceConnector = true;

  /**
   * Defines whether to show the compare.
   */
  @Prop() showCompare = true;

  @State() subcategoryAllData = null;

  @State() recentlyViewedConfig: RecentlyViewedConfig = null;

  @State() recentlyViewedProducts: IProductsCarouselData = {
    sectionTitle: '',
    data: [],
    versionLabel: '',
  };

  @State() atgSubcategoryAllData = null;

  /**
   * Fired on navigation.
   * Contains page parameter as event.detail.
   */
  @Event({
    eventName: MY_SCHNEIDER_CLICK_EVENT,
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    pesLinkClick: EventEmitter<PesRoute>;

  componentWillLoad(): void {
    this.init();
  }

  emitPageViewEvent() {
    send(
      buildPageViewPayload,
      {
        pageLanguage: this.languageCode,
        pageTitle: this.subcategoryAllData.metaTags.analyticsPageData.pageTitle,
        fromWebView: this.subcategoryAllData.metaTags.analyticsPageData.fromWebView,
        businessUnit: this.subcategoryAllData.metaTags.analyticsPageData.businessUnit,
        template: this.subcategoryAllData.metaTags.analyticsPageData.template,
        pageTopCategory: this.subcategoryAllData.metaTags.analyticsPageData.pageTopCategory,
        pageCategory: this.subcategoryAllData.metaTags.analyticsPageData.pageCategory,
        pageSubCategory: this.subcategoryAllData.metaTags.analyticsPageData.pageSubCategory,
        pageSubSubCategory: this.subcategoryAllData.metaTags.analyticsPageData.pageSubSubCategory,
        pageCategoryId: this.subcategoryAllData.metaTags.analyticsPageData.pageCategoryId,
        pageSubCategoryId: this.subcategoryAllData.metaTags.analyticsPageData.pageSubCategoryId,
        pageRangeId: this.subcategoryAllData.metaTags.analyticsPageData.pageRangeId,
        pageProductId: this.subcategoryAllData.metaTags.analyticsPageData.pageProductId,
      },
    );
  }

  private async init(): Promise<void> {
    const [subcategoryAllData, hmcBanner] = await Promise.all([
      this.loadSubcategoryAllData(),
      this.getHmcVisibilityInfo(),
    ]);

    if (!subcategoryAllData) return;

    if (
      subcategoryAllData.configuration?.context?.template
      === SubcategoryContextTemplate.subcategory
    ) {
      (subcategoryAllData as TSubcategoryData).hmcBanner = hmcBanner;
    } else {
      const subcategoryKey = SUBCATEGORY_HMC_KEY.subcategoryListingRanges;
      subcategoryAllData[subcategoryKey].hmcBanner = hmcBanner;
    }

    this.subcategoryAllData = subcategoryAllData;
    this.recentlyViewedConfig = subcategoryAllData.recentlyViewedConfig;
    if (this.subcategoryAllData?.metaTags?.analyticsPageData) this.emitPageViewEvent();

    if (this.showRecentlyViewed && this.recentlyViewedConfig) {
      const { sectionTitle } = this.recentlyViewedConfig;
      this.recentlyViewedProducts = await requestRecentlyViewedProducts(
        this.recentlyViewedConfig?.maxProductsToDisplay || MAX_RECENTLY_VIEW_PRODUCTS,
        sectionTitle,
      );
    }
  }

  private async loadSubcategoryAllData(): Promise<TSubcategoryData | TSubcategoryListingData> {
    try {
      const subcategoriesAllEndpointURL = getExpAPIEndpointURL(
        expAPIEndpoint.SUBCATEGORIES_ALL_SPRYKER,
        {
          subcategoryId: this.subcategoryId,
        },
      );
      const res: TSubcategoryData | TSubcategoryListingData = await getData(
        false,
        subcategoriesAllEndpointURL,
      );

      return res;
    } catch (err) {
      console.error('[PES] Subcategory all request failed', err);
      if (err === HttpStatusCodes.NOT_FOUND) {
        this.pesLinkClick.emit({ page: pesCatalogPageTypes.ALL_PRODUCTS });
      }
    }
  }

  private async getHmcVisibilityInfo(): Promise<THMCBanner> {
    try {
      const { error, result } = await HmcSubcategories.fetchSingleSubcategoryWithHMC({
        countryCode: this.countryCode,
        languageCode: this.languageCode,
        queryParams: {
          pimId: this.subcategoryId,
          platform: HMCPlatform.MySchneider,
        },
      });

      if (error) {
        throw error;
      }

      return result.hmcBanner;
    } catch (err) {
      console.error(`[PES]: HMC visibility api call failed ${err.message || err}`);
    }
  }

  render(): HTMLElement {
    if (!this.subcategoryAllData) {
      return <pdp-skeleton />;
    }

    const isListingPage = this.subcategoryAllData?.configuration?.context?.template
      !== SubcategoryContextTemplate.subcategory;
    return (
      <Fragment>
        { !isListingPage ? (
          <pes-app-subcategory
            host={this.host}
            subcategoryId={this.subcategoryId}
            subcategoryData={this.subcategoryAllData as TSubcategoryData}
            showBreadcrumbs={this.showBreadcrumbs}
            countryCode={this.countryCode}
            languageCode={this.languageCode}
          />
        ) : (
          <pes-app-spryker-subcategory-listing
            host={this.host}
            subcategoryData={this.subcategoryAllData as TSubcategoryListingData}
            showBreadcrumbs={this.showBreadcrumbs}
            subcategoryId={this.subcategoryId}
            showDocuments={this.showDocuments}
            enableCommerceConnector={this.enableCommerceConnector}
            showBuyOnline={this.showBuyOnline}
            showCompare={this.showCompare}
            isPBS={true}
            productCtas={this.productCtas}
            countryCode={this.countryCode}
            languageCode={this.languageCode}
          />
        )}
        <pes-products-carousel
          {...this.recentlyViewedProducts}
          hostWithLocale={`${this.host}${this.countryCode}/${this.languageCode}/`}
          pageType={PageNames.SUBCATEGORY}
          pageId={this.subcategoryId}
          class="pes-app-subcategory__section"
        ></pes-products-carousel>
      </Fragment>
    );
  }
}
